import api from '@/api';
import {
  ContentsContext,
  IndustryData,
  SearchData,
  TrackingBaseData,
} from '@/api/tracking';
import { AxiosError } from 'axios';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { AdpDocument, ArticleMarkSource, Group, GroupMark } from '@/types';

type CreateGroupMarkProps = {
  adpDocument: AdpDocument;
  group: Group;
  themeId?: number;
  tags?: string[];
  memo?: string;
};

type RemoveGroupMarkProps = {
  adpDocument: AdpDocument;
  group: Group;
};

export const useGroupMark = (
  props: {
    // 計測
    markSource?: ArticleMarkSource;
    trackingBaseData?: TrackingBaseData;
    rankInWholeFeed?: number;
    searchData?: SearchData;
    industryData?: IndustryData;
    trendCompanyText?: string;
    contentsContext?: ContentsContext;
    execution?: string;
  } = {},
) => {
  const { createSnackbar } = useSnackbar();

  const createGroupMark = async ({
    adpDocument,
    group,
    themeId,
    tags,
    memo,
  }: CreateGroupMarkProps) => {
    try {
      const groupMarkResponse = await api.postGroupMark(
        group.id,
        adpDocument,
        themeId ?? null,
        props.markSource,
        props.trackingBaseData ?? {},
        props.rankInWholeFeed,
        memo,
        tags,
        props.searchData,
        props.industryData,
        props.trendCompanyText,
        props.contentsContext,
        props.execution,
      );

      await updateTagsAndMemo({
        group,
        adpDocument,
        tags,
        memo,
        isCreate: true,
        hideSnackbar: true,
      });

      const createdGroupMark: GroupMark = {
        ...groupMarkResponse,
        group_name: group.name ?? '',
      };
      adpDocument.group_marks.push(createdGroupMark);

      createSnackbar({
        type: 'link',
        message: group.name,
        params: {
          link: `/groups/${group.id}/marks`,
          suffixText: 'にグループマークしました',
        },
      });
      return true;
    } catch (e) {
      createSnackbar({
        message: 'グループマークできませんでした',
        type: 'error',
      });
      throw e;
    }
  };

  const updateTagsAndMemo = async ({
    group,
    adpDocument,
    tags,
    memo,
    isCreate = false,
    hideSnackbar,
  }: CreateGroupMarkProps & {
    isCreate?: boolean;
    hideSnackbar?: boolean;
  }) => {
    // 作成の場合は、tagsとmemoの比較が不要
    // 理由：マークとグループマークのtagsとmemoはadpDocumentの同じキーに保存されるので
    //      マークとグループマークのタグとメモが同じになる時、更新するリクエストを投げていない
    const tagMemoRequests: Promise<void>[] = [];
    try {
      const isUpdatingTags =
        tags &&
        tags.length > 0 &&
        (isCreate || tags?.toString() !== adpDocument.tags?.toString());
      const isRemovingAllTags =
        tags !== undefined &&
        tags.length === 0 &&
        adpDocument.tags &&
        adpDocument.tags?.length > 0;
      if (isUpdatingTags) {
        tagMemoRequests.push(
          api.updateGroupArticleTags(group.id, adpDocument, tags),
        );
        adpDocument.tags = [...tags];
      } else if (isRemovingAllTags) {
        tagMemoRequests.push(
          api.updateGroupArticleTags(group.id, adpDocument, []),
        );
        adpDocument.tags = [];
      }

      const isUpdatingMemo =
        memo && memo.length > 0 && (isCreate || memo !== adpDocument.memo);
      const isRemovingMemo =
        memo !== undefined &&
        memo.length === 0 &&
        adpDocument.memo &&
        adpDocument.memo?.length > 0;
      if (isUpdatingMemo) {
        tagMemoRequests.push(
          api.updateGroupArticleMemo(group.id, adpDocument, memo),
        );
        adpDocument.memo = memo;
      } else if (isRemovingMemo) {
        tagMemoRequests.push(
          api
            .deleteGroupArticleMemo(group.id, adpDocument)
            .catch((e: AxiosError) => {
              if (e.response?.status !== 404) {
                throw e;
              }
            }),
        );
        adpDocument.memo = '';
      }

      if (
        isUpdatingTags ||
        isRemovingAllTags ||
        isUpdatingMemo ||
        isRemovingMemo
      ) {
        await Promise.all(tagMemoRequests);
        await api.trackEvent(
          'set_article_option',
          props.trackingBaseData ?? {},
          adpDocument,
          group.id,
          {
            tags: tags,
            memo: memo,
          },
        );
      }

      if (!hideSnackbar) {
        createSnackbar({
          message: '記事情報を更新しました',
          type: 'success',
        });
      }
    } catch (e) {
      if (!hideSnackbar) {
        createSnackbar({
          message: '記事情報を更新できませんでした',
          type: 'error',
        });
      }
      throw e;
    }
  };

  const removeGroupMark = async ({
    adpDocument,
    group,
  }: RemoveGroupMarkProps) => {
    try {
      const index = adpDocument.group_marks.findIndex(
        gm => gm.group_id === group.id,
      );
      if (index === -1) {
        return;
      }
      await api.deleteGroupMark(adpDocument.group_marks[index].id);
      adpDocument.group_marks.splice(index, 1);
      createSnackbar({
        message: 'グループマークを外しました',
        type: 'success',
      });
      return true;
    } catch (e) {
      createSnackbar({
        message: 'グループマークを外せませんでした',
        type: 'error',
      });
      throw e;
    }
  };

  return { createGroupMark, updateTagsAndMemo, removeGroupMark };
};
